/**
 * Место съёмки не уходит с файлом (МП-07): из JPEG убираются блоки EXIF и XMP (там GPS телефона), из MP4/MOV —
 * записи места (`©xyz`, `loci`, ключи `com.apple.quicktime.location.*`). Место, если человек согласился,
 * хранится только в записи отчёта. Размер и порядок данных ролика не меняются: запись места превращается в пустой
 * блок `free` той же длины, поэтому ссылки внутри файла остаются верными.
 * Непонятный файл возвращается как есть: фото после сжатия на холсте метаданных и так не несёт.
 */

const text = (bytes: Uint8Array, at: number, length: number) =>
  String.fromCharCode(...bytes.subarray(at, Math.min(bytes.length, at + length)));

/** JPEG без блоков EXIF/XMP/IPTC; null — не JPEG или разобрать не удалось. */
export function stripJpegLocation(bytes: Uint8Array): Uint8Array | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  const keep: Array<[number, number]> = [[0, 2]];
  let at = 2;
  let changed = false;
  while (at + 4 <= bytes.length) {
    if (bytes[at] !== 0xff) return null;
    const marker = bytes[at + 1]!;
    if (marker === 0xff) {
      at += 1; // байт-заполнитель
      continue;
    }
    // Начало данных снимка: дальше метаданных нет, остаток копируется как есть
    if (marker === 0xda) {
      keep.push([at, bytes.length]);
      break;
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      keep.push([at, at + 2]);
      at += 2;
      continue;
    }
    const length = (bytes[at + 2]! << 8) | bytes[at + 3]!;
    if (length < 2 || at + 2 + length > bytes.length) return null;
    const payload = text(bytes, at + 4, 29);
    const drop =
      (marker === 0xe1 && (payload.startsWith('Exif\0') || payload.startsWith('http://ns.adobe.com/xap'))) ||
      marker === 0xed;
    if (drop) changed = true;
    else keep.push([at, at + 2 + length]);
    at += 2 + length;
  }
  if (!changed) return bytes;
  const out = new Uint8Array(keep.reduce((n, [a, b]) => n + (b - a), 0));
  let pos = 0;
  for (const [a, b] of keep) {
    out.set(bytes.subarray(a, b), pos);
    pos += b - a;
  }
  return out;
}

const CONTAINERS = new Set(['moov', 'trak', 'udta', 'meta', 'mdia', 'minf', 'ilst']);
const LOCATION_BOXES = new Set(['©xyz', 'loci']);
const FREE = [0x66, 0x72, 0x65, 0x65];

interface Box {
  start: number;
  header: number;
  end: number;
  type: string;
}

function boxes(bytes: Uint8Array, from: number, to: number): Box[] | null {
  const out: Box[] = [];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let at = from;
  while (at + 8 <= to) {
    let size = view.getUint32(at);
    let header = 8;
    if (size === 1) {
      if (at + 16 > to) return null;
      const big = view.getBigUint64(at + 8);
      if (big > BigInt(to - at)) return null;
      size = Number(big);
      header = 16;
    } else if (size === 0) size = to - at;
    if (size < header || at + size > to) return null;
    out.push({ start: at, header, end: at + size, type: text(bytes, at + 4, 4) });
    at += size;
  }
  return out;
}

function blank(bytes: Uint8Array, box: Box) {
  bytes.set(FREE, box.start + 4);
  bytes.fill(0, box.start + box.header, box.end);
}

/** В `meta` QuickTime: номера ключей, у которых имя про место съёмки. */
function locationKeys(bytes: Uint8Array, keys: Box): Set<number> {
  const found = new Set<number>();
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // keys: версия и флаги (4), число записей (4), затем записи [размер 4][пространство 4][имя]
  let at = keys.start + keys.header + 8;
  let index = 1;
  while (at + 8 <= keys.end) {
    const size = view.getUint32(at);
    if (size < 8 || at + size > keys.end) break;
    const name = text(bytes, at + 8, size - 8).toLowerCase();
    if (name.includes('location') || name.includes('gps')) {
      found.add(index);
      bytes.fill(0x5f, at + 8, at + size); // имя ключа больше не говорит, что здесь было место
    }
    at += size;
    index += 1;
  }
  return found;
}

function walk(bytes: Uint8Array, from: number, to: number, depth: number): boolean {
  if (depth > 8) return false;
  const list = boxes(bytes, from, to);
  if (!list) return false;
  let changed = false;
  let located = new Set<number>();
  for (const box of list) {
    if (box.type === 'keys') located = locationKeys(bytes, box);
  }
  if (located.size) changed = true;
  for (const box of list) {
    if (LOCATION_BOXES.has(box.type)) {
      blank(bytes, box);
      changed = true;
      continue;
    }
    if (box.type === 'ilst' && located.size) {
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      for (const item of boxes(bytes, box.start + box.header, box.end) ?? []) {
        if (located.has(view.getUint32(item.start + 4))) {
          blank(bytes, item);
          changed = true;
        }
      }
      continue;
    }
    if (!CONTAINERS.has(box.type)) continue;
    let inner = box.start + box.header;
    // ISO meta — «полный» блок с 4 байтами версии перед вложенными; у QuickTime их нет
    if (box.type === 'meta' && !['hdlr', 'keys', 'ilst', 'free'].includes(text(bytes, inner + 4, 4)))
      inner += 4;
    if (walk(bytes, inner, box.end, depth + 1)) changed = true;
  }
  return changed;
}

/** MP4/MOV без записей места (копия); null — не ролик ISO/QuickTime или разобрать не удалось. */
export function stripVideoLocation(input: Uint8Array): Uint8Array | null {
  if (input.length < 8) return null;
  const first = text(input, 4, 4);
  if (!['ftyp', 'moov', 'mdat', 'wide', 'free', 'skip'].includes(first)) return null;
  const bytes = input.slice();
  return walk(bytes, 0, bytes.length, 0) ? bytes : input;
}

/** Убрать место съёмки из файла; если менять нечего — тот же Blob (контрольная сумма не меняется). */
export async function scrubMediaLocation(blob: Blob): Promise<Blob> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const out = stripJpegLocation(bytes) ?? stripVideoLocation(bytes);
  if (!out || out === bytes) return blob;
  return new Blob([out as Uint8Array<ArrayBuffer>], { type: blob.type });
}

/** Для проверки: есть ли в файле запись места (EXIF/XMP в JPEG, `©xyz`/`loci`/ключ места в ролике). */
export function hasMediaLocation(bytes: Uint8Array): boolean {
  const jpeg = stripJpegLocation(bytes);
  if (jpeg) return jpeg !== bytes;
  const video = stripVideoLocation(bytes);
  return Boolean(video && video !== bytes);
}
